import { DesktopOnly } from '@bp/ui-components';
import { t } from 'i18next';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { RequireModule } from '../../../components/LicenseHint/RequireModule';
import {
  TimetableVersionMetaDocument,
  TimetableVersionMetaQuery,
  TimetableVersionMetaQueryVariables,
} from '../../../types/planung-graphql-client-defs';
import { addLeadingZero } from '../../../utils/helper';
import { filterLoader } from '../../../utils/routes-loader/filterLoader';
import { teachingBlockVersionLoader } from '../../../utils/routes-loader/teachingBlockVersionLoader';
import { urqlClient } from '../../../utils/urqlClient';
import { TeachingBlockVersionBoard } from './TeachingBlockVersion/Board/TeachingBlockVersionBoard';
import { PlanTeachingBlockClasses } from './TeachingBlockVersion/Data/Classes/PlanTeachingBlockClasses';
import { PlanTeachingBlockDependencies } from './TeachingBlockVersion/Data/Dependencies/PlanTeachingBlockDependencies';
import { PlanTeachingBlockLessons } from './TeachingBlockVersion/Data/Lessons/PlanTeachingBlockLessons';
import { PlanTeachingBlockRooms } from './TeachingBlockVersion/Data/Rooms/PlanTeachingBlockRooms';
import { PlanTeachingBlockSubjects } from './TeachingBlockVersion/Data/Subjects/PlanTeachingBlockSubjects';
import { PlanTeachingBlockTeachers } from './TeachingBlockVersion/Data/Teachers/PlanTeachingBlockTeachers';
import { TeachingBlockVersionData } from './TeachingBlockVersion/Data/TeachingBlockVersionData';
import { TeachingBlockVersion } from './TeachingBlockVersion/TeachingBlockVersion';
import { TimetablePlanPage } from './TimetablePlanPage';
import { Board } from './TimetableVersion/Board/Board';
import { PlanTimetableClasses } from './TimetableVersion/Data/Classes/PlanTimetableClasses';
import { PlanTimetableConstraintSettings } from './TimetableVersion/Data/ConstraintSettings/PlanTimetableConstraintSettings';
import { PlanTimetableDependencies } from './TimetableVersion/Data/Dependencies/PlanTimetableDependencies';
import { PlanTimetableLessons } from './TimetableVersion/Data/Lessons/PlanTimetableLessons';
import { PlanTimetableRooms } from './TimetableVersion/Data/Rooms/PlanTimetableRooms';
import { PlanTimetableSubjects } from './TimetableVersion/Data/Subjects/PlanTimetableSubjects';
import { PlanTimetableTeachers } from './TimetableVersion/Data/Teachers/PlanTimetableTeachers';
import { TimetableVersionData } from './TimetableVersion/Data/TimetableVersionData';
import { TimetableVersion } from './TimetableVersion/TimetableVersion';

export const timetablePlanRoutes = (baseSlug: string): RouteObject[] => {
  return [
    {
      element: <TimetablePlanPage />,
      index: true,
    },
    // TIMETABLE
    {
      path: t('routes.versions.slug'),
      element: <Outlet />,
      children: [
        // ohne Versions-UUID gibt es keine eigene Seite → zurück zur Planungsübersicht
        { index: true, element: <Navigate to='..' replace /> },
        {
          path: ':versionUuid',
          id: 'version',
          element: <TimetableVersion baseSlug={baseSlug} />,
          loader: ({ params }) => {
            return urqlClient
              .query<TimetableVersionMetaQuery, TimetableVersionMetaQueryVariables>(TimetableVersionMetaDocument, {
                uuid: params.versionUuid ?? '',
              })
              .toPromise();
          },
          handle: {
            crumb: ({ data }: { data?: TimetableVersionMetaQuery }) => {
              const version = data?.timetableVersion;
              if (!version) return [];
              return [
                {
                  slug: `${baseSlug}/${t('routes.versions.slug')}/${version.versionUuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
                  title: `${version.timetableName} - ${addLeadingZero(version.version)}`,
                },
              ];
            },
          },
          children: [
            {
              path: t('routes.versionPages.data.slug'),
              element: <TimetableVersionData />,
              children: [
                {
                  path: t('routes.versionsFilter.class.slug'),
                  element: <PlanTimetableClasses />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.class.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.subject.slug'),
                  element: <PlanTimetableSubjects />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.subject.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.person.slug'),
                  element: <PlanTimetableTeachers />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.person.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.room.slug'),
                  element: <PlanTimetableRooms />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.room.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.lesson.slug'),
                  element: <PlanTimetableLessons />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.lesson.title'),
                    }),
                  },
                  loader: filterLoader,
                },
                {
                  path: t('teachingBlockSubjectContainer.routes.timetableVersion.slug'),
                  element: (
                    <RequireModule enabled='epochen' hint={{ kind: 'teachingBlocks' }}>
                      <PlanTimetableLessons subjectContainerLessons={true} />
                    </RequireModule>
                  ),
                  handle: {
                    crumb: () => ({
                      title: t('teachingBlockSubjectContainer.routes.timetableVersion.title'),
                    }),
                  },
                  loader: filterLoader,
                },
                {
                  path: t('routes.versionsFilter.dependency.slug'),
                  element: <PlanTimetableDependencies />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.dependency.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.constraintSettings.slug'),
                  element: <PlanTimetableConstraintSettings />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.constraintSettings.title'),
                    }),
                  },
                },
              ],
            },
            {
              path: t('routes.versionPages.board.slug'),
              element: (
                <DesktopOnly title={t('desktopOnly.title')} subtitle={t('desktopOnly.subtitle')}>
                  <Board />
                </DesktopOnly>
              ),
              handle: {
                crumb: () => ({
                  title: t('routes.versionPages.board.title'),
                }),
              },
            },
          ],
        },
      ],
    },

    // TEACHINGBLOCK
    {
      path: t('routes.teachingBlockVersions.slug'),
      element: <Outlet />,
      children: [
        { index: true, element: <Navigate to='..' replace /> },
        {
          path: ':teachingBlockVersionUuid',
          id: 'teachingBlockVersion',
          element: (
            <RequireModule enabled='epochen' hint={{ kind: 'teachingBlocks' }}>
              <TeachingBlockVersion baseSlug={baseSlug} />
            </RequireModule>
          ),
          loader: teachingBlockVersionLoader,
          handle: {
            crumb: (loaderResponse: { subjectContainerName: string; versionName: string; versionUuid: string }) => {
              return [
                {
                  slug: `${baseSlug}/${t('routes.teachingBlockVersions.slug')}/${loaderResponse.versionUuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
                  title: `${loaderResponse.subjectContainerName} - ${loaderResponse.versionName}`,
                },
              ];
            },
          },
          children: [
            {
              path: t('routes.versionPages.data.slug'),
              element: <TeachingBlockVersionData />,
              children: [
                {
                  path: t('routes.versionsFilter.class.slug'),
                  element: <PlanTeachingBlockClasses />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.class.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.subject.slug'),
                  element: <PlanTeachingBlockSubjects />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.subject.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.person.slug'),
                  element: <PlanTeachingBlockTeachers />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.person.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.room.slug'),
                  element: <PlanTeachingBlockRooms />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.room.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.lesson.slug'),
                  element: <PlanTeachingBlockLessons />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.lesson.title'),
                    }),
                  },
                  loader: filterLoader,
                },
                {
                  path: t('routes.versionsFilter.dependency.slug'),
                  element: <PlanTeachingBlockDependencies />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.dependency.title'),
                    }),
                  },
                },
              ],
              loader: ({ params }) => {
                return { activeTimetableVersionTab: 'data' };
              },
            },
            {
              path: t('routes.versionPages.board.slug'),
              element: (
                <DesktopOnly title={t('desktopOnly.title')} subtitle={t('desktopOnly.subtitle')}>
                  <TeachingBlockVersionBoard />
                </DesktopOnly>
              ),
              loader: () => {
                return { activeTimetableVersionTab: 'board' };
              },
            },
          ],
        },
      ],
    },
  ];
};
